import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

// --- Shared styled primitives ---

const Row = styled.div`
  display: flex;
  gap: ${props => props.$gap || '14px'};
  ${props => props.$vertical && `
    flex-direction: column;
    gap: 6px;
  `}
`;

const MeterItem = styled.div`
  ${props => props.$full ? 'flex: 1;' : ''}
  display: flex;
  ${props => props.$full ? 'flex-direction: column; gap: 6px;' : 'flex-wrap: wrap; align-items: center; gap: 5px;'}
  font-size: ${props => props.$full ? '0.78rem' : '0.68rem'};
  color: rgba(255, 255, 255, ${props => props.$full ? '0.5' : '0.45'});
`;

const MeterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
`;

const BarTrack = styled.div`
  width: ${props => props.$width || '48px'};
  height: ${props => props.$height || '5px'};
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  position: relative;
  overflow: hidden;
`;

const BarFill = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
  ${props => props.$bipolar ? `
    left: ${props.$value < 0 ? `${50 + props.$value / 2}%` : '50%'};
    width: ${Math.abs(props.$value) / 2}%;
    background: ${props.$value < 0 ? '#E94B6E' : '#4CAF50'};
  ` : `
    left: 0;
    width: ${props.$value}%;
    background: ${props.$value >= 70 ? '#4CAF50' : props.$value >= 40 ? '#81C784' : '#E94B6E'};
  `}
`;

const BarCenter = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
`;

const ValueLabel = styled.span`
  font-weight: 600;
  font-size: ${props => props.$fontSize || '0.62rem'};
  min-width: ${props => props.$minWidth || '22px'};
  text-align: right;
  color: ${props => props.$color || 'rgba(255, 255, 255, 0.5)'};
`;

const ExpandBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  transition: all 0.15s ease;
  transform: rotate(${props => props.$open ? '180deg' : '0deg'});
  &:hover { color: rgba(255, 255, 255, 0.6); }
`;

const ReasonText = styled.div`
  width: 100%;
  font-size: ${props => props.$full ? '0.82rem' : '0.78rem'};
  color: rgba(255, 255, 255, ${props => props.$full ? '0.55' : '0.5'});
  line-height: 1.45;
  padding-left: 2px;
  margin-top: 2px;
`;

// --- Size presets ---

const SIZE_CONFIG = {
  compact:  { trackWidth: '48px', trackHeight: '5px', valueFontSize: '0.62rem', valueMinWidth: '22px', gap: '14px' },
  medium:   { trackWidth: '60px', trackHeight: '6px', valueFontSize: '0.65rem', valueMinWidth: '28px', gap: '12px' },
  full:     { trackWidth: '100%', trackHeight: '6px', valueFontSize: '0.75rem', valueMinWidth: '30px', gap: '20px' },
};

// --- Color helpers ---

function researchColor(value) {
  if (value > 0) return '#4CAF50';
  if (value < 0) return '#E94B6E';
  return 'rgba(255,255,255,0.35)';
}

function qualityColor(value) {
  if (value >= 70) return '#4CAF50';
  if (value >= 40) return '#81C784';
  return '#E94B6E';
}

/**
 * ScoreMeters — Shared research support + quality meters.
 *
 * Used in LensItemCard, LensItemDetail, AILensItemsModal, ImpactPathNodeModal.
 *
 * @param {Object} scores - { research_support, research_support_reason, quality, quality_reason }
 * @param {'compact'|'medium'|'full'} size - Track width preset
 * @param {boolean} expandable - Show chevron to toggle reason text
 * @param {boolean} expanded - Always show reason text (overrides expandable)
 * @param {string} expandedKey - External expanded state key (for multi-card expand tracking)
 * @param {function} onToggleExpand - External toggle handler (key, event) => void
 */
export default function ScoreMeters({
  scores,
  size = 'compact',
  expandable = false,
  expanded = false,
  expandedKey,
  onToggleExpand,
}) {
  const { t } = useTranslation();
  const [localExpanded, setLocalExpanded] = useState(null); // 'research' | 'quality' | null

  if (!scores) return null;

  const cfg = SIZE_CONFIG[size] || SIZE_CONFIG.compact;
  const isFull = size === 'full';
  const researchLabel = t('aiScorePanel.researchSupport');
  const relevanceLabel = t('aiScorePanel.relevance');

  // Expand logic — external or internal
  const isExpanded = (key) => {
    if (expanded) return true;
    if (onToggleExpand && expandedKey != null) {
      // External control (AILensItemsModal uses Set-based expand tracking)
      return false; // Caller checks externally
    }
    return localExpanded === key;
  };

  const handleToggle = (key, e) => {
    e.stopPropagation();
    if (onToggleExpand) {
      onToggleExpand(key, e);
      return;
    }
    setLocalExpanded(prev => prev === key ? null : key);
  };

  const hasResearchValue = typeof scores.research_support === 'number' && Number.isFinite(scores.research_support);
  const researchVal = hasResearchValue ? scores.research_support : 0;
  const qualityVal = scores.quality ?? 0;

  return (
    <Row $gap={cfg.gap}>
      {/* Research Support — bipolar -100..+100 */}
      <MeterItem $full={isFull}>
        {isFull ? (
          <>
            <MeterHeader>
              {researchLabel}
              <ValueLabel
                $fontSize={cfg.valueFontSize}
                $minWidth={cfg.valueMinWidth}
                $color={hasResearchValue ? researchColor(researchVal) : 'rgba(255,255,255,0.45)'}
              >
                {hasResearchValue ? `${researchVal > 0 ? '+' : ''}${researchVal}` : '?'}
              </ValueLabel>
            </MeterHeader>
            <BarTrack $width={cfg.trackWidth} $height={cfg.trackHeight}>
              {hasResearchValue && <BarCenter />}
              {hasResearchValue && <BarFill $bipolar $value={researchVal} />}
            </BarTrack>
            {expanded && scores.research_support_reason && (
              <ReasonText $full>{scores.research_support_reason}</ReasonText>
            )}
          </>
        ) : (
          <>
            {researchLabel}
            <BarTrack $width={cfg.trackWidth} $height={cfg.trackHeight}>
              {hasResearchValue && <BarCenter />}
              {hasResearchValue && <BarFill $bipolar $value={researchVal} />}
            </BarTrack>
            <ValueLabel
              $fontSize={cfg.valueFontSize}
              $minWidth={cfg.valueMinWidth}
              $color={hasResearchValue ? researchColor(researchVal) : 'rgba(255,255,255,0.45)'}
            >
              {hasResearchValue ? `${researchVal > 0 ? '+' : ''}${researchVal}` : '?'}
            </ValueLabel>
            {expandable && scores.research_support_reason && (
              <ExpandBtn $open={isExpanded('research')} onClick={(e) => handleToggle('research', e)}>
                <ChevronDown size={size === 'medium' ? 12 : 11} />
              </ExpandBtn>
            )}
            {isExpanded('research') && scores.research_support_reason && (
              <ReasonText>{scores.research_support_reason}</ReasonText>
            )}
          </>
        )}
      </MeterItem>

      {/* Quality — linear 0..100 */}
      <MeterItem $full={isFull}>
        {isFull ? (
          <>
            <MeterHeader>
              {relevanceLabel}
              <ValueLabel $fontSize={cfg.valueFontSize} $minWidth={cfg.valueMinWidth} $color={qualityColor(qualityVal)}>
                {qualityVal}
              </ValueLabel>
            </MeterHeader>
            <BarTrack $width={cfg.trackWidth} $height={cfg.trackHeight}>
              <BarFill $value={qualityVal} />
            </BarTrack>
            {expanded && scores.quality_reason && (
              <ReasonText $full>{scores.quality_reason}</ReasonText>
            )}
          </>
        ) : (
          <>
            {relevanceLabel}
            <BarTrack $width={cfg.trackWidth} $height={cfg.trackHeight}>
              <BarFill $value={qualityVal} />
            </BarTrack>
            <ValueLabel $fontSize={cfg.valueFontSize} $minWidth={cfg.valueMinWidth} $color={qualityColor(qualityVal)}>
              {qualityVal}
            </ValueLabel>
            {expandable && scores.quality_reason && (
              <ExpandBtn $open={isExpanded('quality')} onClick={(e) => handleToggle('quality', e)}>
                <ChevronDown size={size === 'medium' ? 12 : 11} />
              </ExpandBtn>
            )}
            {isExpanded('quality') && scores.quality_reason && (
              <ReasonText>{scores.quality_reason}</ReasonText>
            )}
          </>
        )}
      </MeterItem>
    </Row>
  );
}
